class TrDropdown extends HTMLElement {
	selectors = {
		trigger: '[data-tr-js-dropdown-trigger]',
		list: '[data-tr-js-dropdown-list]',
		input: '[data-tr-js-dropdown-input]',
		value: '[data-tr-js-dropdown-value]',
		option: '[data-tr-js-dropdown-option]',
		boundary: '[data-tr-js-dropdown-boundary]',
	};

	stateClasses = {
		isOpen: 'is-open',
	};

	connectedCallback() {
		this.triggerElement = this.querySelector(this.selectors.trigger);
		this.listElement = this.querySelector(this.selectors.list);
		this.inputElement = this.querySelector(this.selectors.input);
		this.valueElement = this.querySelector(this.selectors.value);
		this.optionElements = Array.from(this.querySelectorAll(this.selectors.option));
		this.boundaryElement = this.closest(this.selectors.boundary);

		this.bindEvents();
	}

	disconnectedCallback() {
		this.destroy();
	}

	bindEvents() {
		this.triggerElement.addEventListener('click', this.onTriggerClick);
		this.optionElements.forEach((optionElement) => optionElement.addEventListener('click', this.onOptionClick));
		this.addEventListener('keydown', this.onKeyDown);
		this.addEventListener('focusout', this.onFocusOut);
	}

	onTriggerClick = () => {
		if (this.listElement.hidden || this.isClosing) {
			this.open();
		} else {
			this.close();
		}
	};

	onOptionClick = (event) => {
		this.select(event.currentTarget);
	};

	onDocumentPointerDown = (event) => {
		if (!this.contains(event.target)) this.close();
	};

	onFocusOut = (event) => {
		if (!this.contains(event.relatedTarget)) this.close();
	};

	onKeyDown = (event) => {
		if (event.key === 'Escape' && !this.listElement.hidden) {
			event.preventDefault();
			event.stopPropagation();
			this.close();
			this.triggerElement.focus();
			return;
		}

		if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

		event.preventDefault();
		if (this.listElement.hidden || this.isClosing) this.open(true);

		const currentIndex = this.optionElements.indexOf(document.activeElement);
		const lastIndex = this.optionElements.length - 1;
		let nextIndex = currentIndex;

		if (event.key === 'Home') nextIndex = 0;
		if (event.key === 'End') nextIndex = lastIndex;
		if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, lastIndex);
		if (event.key === 'ArrowUp') nextIndex = currentIndex < 0 ? lastIndex : Math.max(currentIndex - 1, 0);

		this.optionElements[nextIndex]?.focus();
	};

	open(focusSelected = false) {
		if (!this.listElement.hidden && !this.isClosing) return;

		this.isClosing = false;
		if (this.listElement.hidden) {
			this.listElement.hidden = false;
			this.positionList();
		}
		this.openAnimationFrame = requestAnimationFrame(() => {
			this.classList.add(this.stateClasses.isOpen);
		});
		this.triggerElement.setAttribute('aria-expanded', 'true');
		document.addEventListener('pointerdown', this.onDocumentPointerDown);

		const selectedOption = this.optionElements.find((optionElement) => optionElement.getAttribute('aria-selected') === 'true') || this.optionElements[0];
		if (!selectedOption) return;

		this.listElement.scrollTop = selectedOption.offsetTop - (this.listElement.clientHeight - selectedOption.clientHeight) / 2;
		if (focusSelected) selectedOption.focus({ preventScroll: true });
	}

	async close() {
		if (this.listElement.hidden || this.isClosing) return;

		cancelAnimationFrame(this.openAnimationFrame);
		this.isClosing = true;
		this.classList.remove(this.stateClasses.isOpen);
		this.triggerElement.setAttribute('aria-expanded', 'false');
		document.removeEventListener('pointerdown', this.onDocumentPointerDown);

		const animations = this.listElement.getAnimations();
		await Promise.allSettled(animations.map((animation) => animation.finished));
		if (this.isClosing) this.finishClose();
	}

	finishClose() {
		this.classList.remove(this.stateClasses.isOpen);
		this.listElement.hidden = true;
		this.listElement.style.maxHeight = '';
		this.removeAttribute('data-tr-placement');
		this.isClosing = false;
	}

	select(optionElement) {
		this.inputElement.value = optionElement.dataset.trJsDropdownOptionValue;
		this.valueElement.textContent = optionElement.textContent.trim();
		this.optionElements.forEach((itemElement) => {
			itemElement.setAttribute('aria-selected', String(itemElement === optionElement));
		});
		this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
		this.close();
		this.triggerElement.focus();
	}

	positionList() {
		const triggerBounds = this.triggerElement.getBoundingClientRect();
		const boundaryBounds = this.boundaryElement?.getBoundingClientRect();
		const availableAbove = triggerBounds.top - (boundaryBounds?.top ?? 0) - 12;
		const availableBelow = (boundaryBounds?.bottom ?? window.innerHeight) - triggerBounds.bottom - 12;
		const opensAbove = availableBelow < this.listElement.getBoundingClientRect().height && availableAbove > availableBelow;
		const availableHeight = opensAbove ? availableAbove : availableBelow;

		this.setAttribute('data-tr-placement', opensAbove ? 'top' : 'bottom');
		this.listElement.style.maxHeight = `${Math.max(80, Math.floor(availableHeight))}px`;
	}

	destroy() {
		this.triggerElement.removeEventListener('click', this.onTriggerClick);
		this.optionElements.forEach((optionElement) => optionElement.removeEventListener('click', this.onOptionClick));
		this.removeEventListener('keydown', this.onKeyDown);
		this.removeEventListener('focusout', this.onFocusOut);
		cancelAnimationFrame(this.openAnimationFrame);
		document.removeEventListener('pointerdown', this.onDocumentPointerDown);
		this.classList.remove(this.stateClasses.isOpen);
		this.finishClose();
	}
}

if (!customElements.get('tr-dropdown')) {
	customElements.define('tr-dropdown', TrDropdown);
}
